// Arena camera geometry shared by the server's view culling (arena/net) and the client camera
// (territory.md §6, visuals §10; WELCOME cellsDiagonal / zoomOutMax).
//
// - The visible world diagonal is `cellsDiagonal × arenaZoomOut(...)` at any aspect.
// - Zoom-out with land: target view scale = 1 / (1 − (1 − 1/zoomOutMax) · easeOutCubic(q)), with
//   q = the land share floored to 5 % steps (ARENA_CLIENT.zoomStep): ×1.05 at 5 %, ×1.41 at 50 %,
//   ×1.50 at 100 % for zoomOutMax 1.5 (the reference's 4.5 → 3.0 scale, territory.md §6).
// - The server does not know a client's aspect, so it culls with a square box wide enough for
//   the widest supported aspect (21:9) in either orientation: half side = D · A / (2 · √(1 + A²)).
//
// Pure. Owner: lane P (the client camera, lane C, may use the same functions).

import { ARENA_CLIENT } from './constants.js';

/** Widest screen aspect the server's view box covers (either orientation). */
export const ARENA_VIEW_MAX_ASPECT = 21 / 9;

function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}

/**
 * The land zoom-out multiplier of the view diagonal: 1 with no land, `zoomOutMax` with all of
 * it, in `ARENA_CLIENT.zoomStep` steps of the land share.
 */
export function arenaZoomOut(landCells: number, playableCells: number, zoomOutMax: number): number {
  if (!(playableCells > 0) || !(landCells > 0) || !(zoomOutMax > 1)) return 1;
  const steps = Math.round(1 / ARENA_CLIENT.zoomStep);
  const share = Math.min(1, landCells / playableCells);
  const q = Math.floor(share * steps + 1e-9) / steps;
  return 1 / (1 - (1 - 1 / zoomOutMax) * easeOutCubic(q));
}

/** Half the side of the square box that holds a view of diagonal `diagonal` at any aspect ≤ 21:9. */
export function arenaViewHalfExtent(diagonal: number): number {
  const a = ARENA_VIEW_MAX_ASPECT;
  return (diagonal * a) / (2 * Math.sqrt(1 + a * a));
}
